/**
 * FeatureDemoSection - Reusable feature showcase grid
 * Displays clickable cards for all major app features.
 * For unauthenticated users, protected cards show a "join to unlock" CTA.
 */

import { Link, useNavigate } from 'react-router-dom';

interface Feature {
  icon: string;
  title: string;
  desc: string;
  link: string;
  label: string;
  color: string;        // Tailwind gradient classes for text
  bg: string;           // card background
  border: string;       // card border
  iconBg: string;       // icon bubble background
  protected?: boolean;  // requires auth
}

const FEATURES: Feature[] = [
  {
    icon: '🔥',
    title: 'Trending Prompts',
    desc: 'Discover the hottest AI prompts curated by the community in real-time.',
    link: '/prompts',
    label: 'Browse Now',
    color: 'from-orange-500 to-red-500',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    iconBg: 'bg-orange-100',
  },
  {
    icon: '💬',
    title: 'Community Chat',
    desc: 'Connect with creators, share ideas, and collab in real-time discussion rooms.',
    link: '/chat',
    label: 'Open Chat',
    color: 'from-violet-500 to-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    iconBg: 'bg-purple-100',
    protected: true,
  },
  {
    icon: '🔖',
    title: 'Save Prompts',
    desc: 'Bookmark your favourite prompts and build a personal library for later use.',
    link: '/saved',
    label: 'My Saved',
    color: 'from-blue-500 to-cyan-500',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    iconBg: 'bg-blue-100',
    protected: true,
  },
  {
    icon: '✏️',
    title: 'Share Prompts',
    desc: "Create and publish your own AI prompts. Help others unlock AI's potential.",
    link: '/prompts/create',
    label: 'Create Prompt',
    color: 'from-emerald-500 to-teal-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    protected: true,
  },
  {
    icon: '📋',
    title: 'My Prompts',
    desc: "Manage all prompts you've created — edit, delete, and track engagement.",
    link: '/my-prompts',
    label: 'View My Prompts',
    color: 'from-amber-500 to-yellow-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    protected: true,
  },
  {
    icon: '🌐',
    title: 'Explore & Request',
    desc: 'Search thousands of prompts. Request custom prompts from the community.',
    link: '/prompts',
    label: 'Explore All',
    color: 'from-pink-500 to-rose-500',
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    iconBg: 'bg-pink-100',
  },
];

interface Props {
  isAuthenticated: boolean;
}

export default function FeatureDemoSection({ isAuthenticated }: Props) {
  const navigate = useNavigate();

  return (
    <section>
      {/* Heading */}
      <div className="text-center mb-10">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Everything you need in one place
        </h2>
        <p className="text-gray-500 text-lg">
          Explore all the ways PromptShare can supercharge your AI workflow
        </p>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {FEATURES.map((feat) => (
          <div
            key={feat.title}
            className={`relative rounded-xl border ${feat.border} ${feat.bg} p-6 flex flex-col gap-4 hover:shadow-md transition-shadow`}
          >
            {/* Lock badge for protected & guest */}
            {feat.protected && !isAuthenticated && (
              <span className="absolute top-3 right-3 text-xs bg-white border border-gray-200 text-gray-500 rounded-full px-2 py-0.5 font-medium">
                🔒 Login required
              </span>
            )}

            {/* Icon */}
            <div className={`w-12 h-12 rounded-xl ${feat.iconBg} flex items-center justify-center text-2xl`}>
              {feat.icon}
            </div>

            {/* Text */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">{feat.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{feat.desc}</p>
            </div>

            {/* CTA */}
            <div className="mt-auto">
              {feat.protected && !isAuthenticated ? (
                <button
                  onClick={() => navigate('/register')}
                  className={`inline-flex items-center gap-1 text-sm font-semibold bg-gradient-to-r ${feat.color} bg-clip-text text-transparent hover:opacity-80 transition-opacity`}
                >
                  Join to unlock →
                </button>
              ) : (
                <Link
                  to={feat.link}
                  className={`inline-flex items-center gap-1 text-sm font-semibold bg-gradient-to-r ${feat.color} bg-clip-text text-transparent hover:opacity-80 transition-opacity`}
                >
                  {feat.label} →
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
